import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import BrandMark from '../BrandMark';
import Icon from '../Icon';
import { useI18n } from '../../i18n/I18nContext';
import { useScrollState } from '../../hooks/useMotion';
import { useOwnerContact } from '../../hooks/useData';
import { buildWhatsappHref } from '../../lib/property';
import { MOLINO } from '../../config/molino';

function SiteHeader({ language, onLanguageChange, theme, onThemeChange }) {
  const { t } = useI18n();
  const location = useLocation();
  const owner = useOwnerContact();
  const { scrolled, hidden } = useScrollState();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  const links = [
    { to: '/terrenos', label: t.navLand },
    { to: MOLINO.path, label: t.navProject },
    { to: '/propiedades', label: t.navProperties },
    { to: '/comprar', label: t.menuBuy },
    { to: '/alquilar', label: t.menuRent },
    { to: '/quienes-somos', label: t.navAbout },
    { to: '/contacto', label: t.navContact },
  ];
  const waHref = buildWhatsappHref(owner.ownerWhatsapp, t.waGeneralMessage);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle('header-hidden', hidden && !menuOpen);
  }, [hidden, menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const toggleButton = toggleRef.current;
    document.body.classList.add('is-locked');
    menuRef.current?.querySelector('a')?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('is-locked');
      document.removeEventListener('keydown', onKeyDown);
      toggleButton?.focus();
    };
  }, [menuOpen]);

  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const languageSwitch = (
    <div className="lang-switch" role="group" aria-label={t.languageLabel}>
      {['es', 'en'].map((code) => (
        <button
          key={code}
          type="button"
          className={language === code ? 'is-active' : ''}
          aria-pressed={language === code}
          onClick={() => onLanguageChange(code)}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
  const headerClass = [
    'site-header',
    scrolled ? 'is-scrolled' : '',
    hidden && !menuOpen ? 'is-hidden' : '',
    menuOpen ? 'is-menu-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <header className={headerClass}>
      <div className="site-header__inner">
        <Link to="/" className="site-header__brand" aria-label={t.navHome}>
          <BrandMark />
        </Link>

        <nav className="site-nav" aria-label={t.navMainLabel}>
          {links.map((link) => (
            <NavLink key={link.to} to={link.to} className="site-nav__link">
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="site-header__tools">
          {languageSwitch}
          <button
            type="button"
            className="icon-button"
            onClick={() => onThemeChange(nextTheme)}
            aria-label={theme === 'dark' ? t.themeToggleToLight : t.themeToggleToDark}
            title={theme === 'dark' ? t.light : t.dark}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} />
          </button>
          {waHref ? (
            <a className="button button--ink button--sm site-header__cta" href={waHref} target="_blank" rel="noreferrer">
              <span>{t.navCta}</span>
              <Icon name="arrowUpRight" size={15} />
            </a>
          ) : (
            <Link className="button button--ink button--sm site-header__cta" to="/contacto">
              <span>{t.navCta}</span>
              <Icon name="arrowUpRight" size={15} />
            </Link>
          )}
          <button
            ref={toggleRef}
            type="button"
            className="menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? t.menuClose : t.menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="menu-toggle__label">{t.menuLabel}</span>
            <span className="menu-toggle__bars" aria-hidden="true"><span /><span /></span>
          </button>
        </div>
      </div>

      <div
        id="mobile-menu"
        ref={menuRef}
        className={`mobile-menu ${menuOpen ? 'is-open' : ''}`}
        aria-hidden={!menuOpen}
        inert={menuOpen ? undefined : ''}
      >
        <nav className="mobile-menu__nav" aria-label={t.navMobileLabel}>
          {[{ to: '/', label: t.navHome }, ...links].map((link, index) => (
            <NavLink key={link.to} to={link.to} end className="mobile-menu__link" style={{ '--i': index }}>
              <span className="mobile-menu__index">{String(index + 1).padStart(2, '0')}</span>
              {link.label}
            </NavLink>
          ))}
        </nav>
        <div className="mobile-menu__footer">
          {waHref ? (
            <a className="button button--accent" href={waHref} target="_blank" rel="noreferrer">
              <Icon name="whatsapp" size={18} />
              <span>{t.navCta}</span>
            </a>
          ) : null}
          {owner.ownerPhone ? (
            <a className="link-underline" href={`tel:${owner.ownerPhone}`}>{owner.ownerPhone}</a>
          ) : null}
          {languageSwitch}
        </div>
      </div>
    </header>
  );
}

export default SiteHeader;
